// ─────────────────────────────────────────────────────────────────────────────
// Quick-entry statuses of the presence sheet (#816): the segmented toggle offers the
// day-to-day statuses directly — everything else goes through the pencil dialog. Labels
// come from the server status definitions via `presenceStatusLabel` (fallback mirrors
// `BuiltInPresenceStatusKeys`); icons and Vuetify palette names are fixed here because
// the toggle's selected-fill CSS keys on the generated `text-<color>` class, which a
// server hex could not produce.
// ─────────────────────────────────────────────────────────────────────────────
import { translate } from '@fondationcss/sdk';
import { PresenceStatus } from '@/sdk/model';
import type { ExamSupervisionStatus } from '@/sdk/dto/exam-supervision';
import { isConfirmedStatus, presenceStatusLabel } from '@/utils/presence-status-display';
import type { StudentRow } from './presence-sheet-row';

export interface QuickStatusOption {
  status: PresenceStatus;
  /** Vuetify palette name — must stay a named color, see the module header. */
  color: string;
  icon: string;
}

/** The segmented toggle's buttons, in display order. */
export const QUICK_STATUS_OPTIONS: QuickStatusOption[] = [
  { status: PresenceStatus.Present, color: 'teal', icon: 'fa-check' },
  { status: PresenceStatus.LateArrival, color: 'orange', icon: 'fa-clock' },
  { status: PresenceStatus.EarlyDeparture, color: 'purple', icon: 'fa-right-from-bracket' },
  { status: PresenceStatus.UnjustifiedAbsence, color: 'error', icon: 'fa-ban' },
];

/**
 * The statuses the toggle leaves out, for the exam supervisor's « Autre statut » menu (#1164): the
 * supervisor records every status a teacher can, but has no competence dialog to reach the rest
 * from. The same confirmed statuses the teacher's dialog lists, in its order.
 */
export const OTHER_SUPERVISION_STATUSES: ExamSupervisionStatus[] = [
  PresenceStatus.JustifiedAbsence,
  PresenceStatus.Discharge,
  PresenceStatus.ComplementaryService,
];

/**
 * The status the « Autre statut » button shows (#1164, #1445): the one of its menu the cell records,
 * or null — the button then only offers the menu.
 */
export function otherStatusOf(row: StudentRow): ExamSupervisionStatus | null {
  return OTHER_SUPERVISION_STATUSES.find(status => isStatusSet(row, status)) ?? null;
}

/**
 * What the exam supervisor reads under the buttons, or null. The status in words when the buttons
 * cannot show it: someone else's mark, which locks the cell (#1164), or one of the menu's statuses.
 * An unconfirmed draft says nothing — it is no mark, and the supervisor may record over it.
 */
export function supervisedCellCaption(row: StudentRow, recordedByAnother: boolean): string | null {
  if (!isRecorded(row) || !row.status || !isConfirmedStatus(row.status)) return null;
  return recordedByAnother || !isQuickStatus(row.status as PresenceStatus) ? presenceStatusLabel(row.status) : null;
}

/** True when the status is one of the quick-entry toggles (the pencil then renders quiet). */
export function isQuickStatus(status: PresenceStatus): boolean {
  return QUICK_STATUS_OPTIONS.some(option => option.status === status);
}

/**
 * Is this cell recorded? Three conditions, none superfluous: the row must not be pending
 * deletion, and it must exist SOMEWHERE — on the server (`existingPresenceId`), in the offline
 * outbox so a cell queued offline stays coloured until it syncs (`offlinePending`), or
 * optimistically since the click so the colour does not wait for the round trip
 * (`recordedLocally`).
 *
 * It lives here, once, because it used to live three times: the « Grille » had it, the « Mur »
 * had it, and the classic sheet had a fourth version inline that omitted `recordedLocally` — so
 * a brand-new presence stayed grey there until the sheet reloaded, which is the very wait the
 * flag exists to remove. Both copies claimed in their comment to be « the exact expression the
 * classic toggle uses », and both were wrong about it.
 */
export function isRecorded(row: StudentRow | undefined): boolean {
  return !!row && !row.toDelete
    && !!(row.existingPresenceId || row.offlinePending || row.recordedLocally);
}

/** `isRecorded`, plus the status the cell actually carries — what a toggle colours on. */
export function isStatusSet(row: StudentRow, status: PresenceStatus): boolean {
  return isRecorded(row) && row.status === status;
}

/** Mirrors `RequiresMinutes` of the built-in catalog: these statuses ask for minutes first. */
export function statusCapturesMinutes(status: PresenceStatus): boolean {
  return status === PresenceStatus.LateArrival || status === PresenceStatus.EarlyDeparture;
}

export interface MinutesDialogConfig {
  title: string;
  icon: string;
  fieldLabel: string;
}

/**
 * Title/icon/field label of the minutes dialog for a minutes-capturing status. The field
 * label matches the edit dialog's wording for a departure (« Minutes d'absence ») and stays
 * « Minutes de retard » for a late arrival.
 */
export function minutesDialogConfig(status: PresenceStatus): MinutesDialogConfig {
  const option = QUICK_STATUS_OPTIONS.find(o => o.status === status);
  return {
    title: presenceStatusLabel(status),
    icon: option?.icon ?? 'fa-clock',
    fieldLabel: status === PresenceStatus.EarlyDeparture ? translate('parcours.teaching.quickEntry.minutesAbsence') : translate('parcours.teaching.quickEntry.minutesLate'),
  };
}

/**
 * The minutes shown on the row under the toggle, without opening the dialog (#816):
 * only for a minutes-capturing status carrying a positive value, empty otherwise.
 */
export function formatRowMinutes(status: PresenceStatus, minutes: number | null | undefined): string {
  if (!statusCapturesMinutes(status)) return '';
  if (minutes === null || minutes === undefined || minutes <= 0) return '';
  return translate('parcours.teaching.quickEntry.minutesFormat', { minutes });
}

/** Text class tinting the row minutes with the status color. */
export function rowMinutesClass(status: PresenceStatus): string {
  if (!statusCapturesMinutes(status)) return '';
  const option = QUICK_STATUS_OPTIONS.find(o => o.status === status);
  return option ? `text-${option.color}` : '';
}

/**
 * The minutes offered as chips in the dialog, in order. The order IS the shortcut: the nth
 * chip is Ctrl+n, which is why the list lives here rather than in the view — the chips and the
 * chords must not be able to disagree.
 */
export const QUICK_MINUTES = [10, 15, 30, 45, 60] as const;

/** « Ctrl + 3 » — what a chip's tooltip says, so the chord is discoverable from the chip. */
export function quickMinutesShortcutLabel(index: number): string {
  return `Ctrl + ${index + 1}`;
}

/**
 * Minutes from a `Ctrl` + digit chord: the nth entry of {@link QUICK_MINUTES}, so the chords
 * and the chips are the same list read two ways.
 *
 * A chord and not a bare digit: the field is a number input with the focus, so typing « 3 »
 * must still type a three.
 */
export function minutesShortcutFor(event: {
  key: string;
  ctrlKey: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
}): number | null {
  if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return null;
  if (!/^[1-9]$/.test(event.key)) return null;
  return QUICK_MINUTES[Number(event.key) - 1] ?? null;
}

/**
 * Normalizes the dialog's raw input: empty/invalid → null (v-model.number can yield '').
 *
 * A negative reads as null too (#995): these minutes are a duration, and `computeHoursTarget`
 * subtracts them from the period, so a negative one inflated the target and the dialog's
 * auto-distribution wrote more hours than the period lasts. The field states the rule and the
 * save is blocked while it holds, so nothing reaches here silently — this is the backstop.
 */
export function parseMinutesInput(raw: number | string | null | undefined): number | null {
  if (raw === null || raw === undefined || raw === '' || Number.isNaN(Number(raw))) return null;
  const minutes = Number(raw);
  return minutes < 0 ? null : minutes;
}
